import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Star, Youtube, Twitch, Instagram, MessageSquare } from "lucide-react";
import { useMentorProfile } from "@/hooks/useMentorProfile";
import { useRole } from "@/hooks/useRole";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MentorOfferingsSection } from "@/components/mentor/profile/MentorOfferingsSection";
import { MentorReviewsSection } from "@/components/mentor/profile/MentorReviewsSection";
import { AchievementsList } from "@/components/mentor/profile/AchievementsList";
import { MentorDigitalContentsSection } from "@/components/mentor/profile/MentorDigitalContentsSection";
import { MentorVideoSection } from "@/components/mentor/profile/MentorVideoSection";
import { MentorCommunitiesSection } from "@/components/mentor/profile/MentorCommunitiesSection";
import { Game } from "@/types/game";
import { TalkToMentorButton } from "@/components/mentor/profile/TalkToMentorButton";
import { MentorWithDetails } from "@/types/mentor";

const MentorSocialLinks = ({ mentor }: { mentor: MentorWithDetails }) => {
    const socialLinks = useMemo(() => [
        { icon: Youtube, url: mentor.social?.youtube, label: "YouTube" },
        { icon: Twitch, url: mentor.social?.twitch, label: "Twitch" },
        { icon: Instagram, url: mentor.social?.instagram, label: "Instagram" },
        { icon: MessageSquare, url: mentor.social?.discord, label: "Discord" },
    ].filter(link => link.url && link.url.trim() !== ''), [mentor.social]);

    if (socialLinks.length === 0) {
        return null;
    }

    return (
        <div>
            <h2 className="text-xl font-bold mb-2">Redes Sociais</h2>
            <div className="grid grid-cols-2 gap-2">
                {socialLinks.map((link) => (
                    <Button
                        key={link.label}
                        variant="outline"
                        className="w-full justify-start text-left bg-[#222A35] border-[#2D3748] hover:bg-[#2d3748]"
                        onClick={() => window.open(link.url, "_blank")}
                    >
                        <link.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>{link.label}</span>
                    </Button>
                ))}
            </div>
        </div>
    );
};


const MentorPublicPage = () => {
  const { mentorId } = useParams<{ mentorId: string }>();
  const { data: mentor, isLoading } = useMentorProfile(mentorId);
  const { authUser } = useRole();

  const isProfileOwner = useMemo(() => {
    if (!authUser || !mentor) return false;
    return mentor.profile_id === authUser.id;
  }, [authUser, mentor]);

  const minPrice = useMemo(() => {
    if (!mentor?.offerings || mentor.offerings.length === 0) return null;
    const minCents = Math.min(...mentor.offerings.map((o) => o.price_cents));
    if (isNaN(minCents)) return null;
    const minReal = minCents / 100;
    return minReal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }, [mentor]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0C1117]">
        <Loader2 className="h-12 w-12 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  if (!mentor) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0C1117]">
        <p className="text-white">Mentor não encontrado</p>
      </div>
    );
  }

  return (
    <div className="bg-[#0C1117] text-white min-h-screen">
      {/* Banner */}
      {mentor.cover_url && (
        <div 
          className="relative h-64 bg-center bg-cover"
        >
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to bottom, transparent 0%, rgba(12, 17, 23, 1) 100%), url(${mentor.cover_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        </div>
      )}

      {/* Content Wrapper */}
      <div className={`container mx-auto px-4 sm:px-6 lg:px-8 ${mentor.cover_url ? "-mt-16" : "pt-12"} pb-16`}>
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          
          {/* Left Sidebar */}
          <aside className="lg:w-1/3 w-full lg:sticky lg:top-8">
            <div className="relative bg-[#131922] border border-[#222A35] rounded-lg">
              {/* Avatar floating above the card */}
              <div className="absolute left-1/2 -translate-x-1/2 -top-12">
                <Avatar className="w-24 h-24 border-4 border-[#131922]">
                  <AvatarImage src={mentor.image_url} alt={mentor.name} />
                  <AvatarFallback>{mentor.name?.[0]}</AvatarFallback>
                </Avatar>
              </div>
              
              {/* Card Content */}
              <div className="pt-16 p-6 space-y-4">
                <div className="text-center">
                  <h1 className="text-2xl font-bold">{mentor.name}</h1>
                  <div className="flex items-center justify-center gap-1 text-yellow-400 my-2">
                    <Star className="w-5 h-5 fill-current" />
                    <span className="text-lg font-semibold">{mentor.average_rating?.toFixed(1) || "0.0"}</span>
                  </div>
                  {minPrice && (
                    <p className="text-sm text-gray-400 mb-4">
                      Mentoria a partir de {minPrice}
                    </p>
                  )}
                </div>

                <TalkToMentorButton mentorId={mentor.profile_id} />
                
                {/* 🎯 Vídeo do YouTube (se configurado) */}
                <MentorVideoSection videoUrl={mentor.video_url} />
                
                <div className="space-y-4 text-left">
                  <hr className="border-t border-[#2D3748]" />
                  <div>
                    <h2 className="text-xl font-bold mb-2">Sobre mim</h2>
                    <p className="text-gray-300 text-sm">{mentor.bio || "Nenhuma biografia fornecida."}</p>
                  </div>

                  <hr className="border-t border-[#2D3748]" />
                  <div>
                    <h2 className="text-xl font-bold mb-2">Conquistas</h2>
                    <AchievementsList achievements={mentor.achievements || []} />
                  </div>
                  
                  <hr className="border-t border-[#2D3748]" />
                  <div>
                    <h2 className="text-xl font-bold mb-2">Jogos</h2>
                    <div className="flex flex-wrap gap-2">
                      {mentor.games?.map((game: Game) => (
                        <Badge key={game.id} variant="secondary" className="bg-[#222A35] text-white hover:bg-[#2d3748]">
                          {game.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <hr className="border-t border-[#2D3748]" />
                  <MentorSocialLinks mentor={mentor} />
                </div>
              </div>
            </div>
          </aside>

          {/* Right Content */}
          <main className="lg:w-2/3 w-full pt-12 space-y-6 pb-8">
            <MentorOfferingsSection mentor={mentor} isProfileOwner={isProfileOwner} />
            
            {/* 🎯 Seção de Comunidades do Mentor */}
            <MentorCommunitiesSection communities={mentor.communities || []} />
            
            <MentorDigitalContentsSection mentorId={mentor.id} />
            <MentorReviewsSection mentorId={mentor.profile_id} />
          </main>
        </div>
      </div>
    </div>
  );
};

export default MentorPublicPage;